import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  AttendanceTrackingCrumb,
  AttendanceTrackingPageHeader
} from 'components/molecules/AttendanceTrackingPageHeader';
import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import {
  addMonths,
  format,
  isSameMonth,
  startOfMonth,
  subMonths
} from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Waypoint } from 'react-waypoint';
import {
  formatTooltipHours,
  formatViewRangeLabel,
  getMonthsInView,
  STATUS_LABELS,
  WEEKDAYS,
  type DayRecord,
  type DayStatus
} from './calendarShared';
import {
  buildMonthCells,
  computeSummary,
  getCellModifier,
  getRecordsForMonth,
  getRecordsInRange,
  hasPunchDetails,
  positionTooltipNearCell,
  type CalendarCell,
  type CalendarViewMode,
  type MonthSection
} from './calendarUtils';
import { CalendarMonthNavigator } from './components/CalendarMonthNavigator';
import { CalendarViewModeToggle } from './components/CalendarViewModeToggle';
import { OfficeDaysProgressBar } from './components/OfficeDaysProgressBar';
import {
  AttendanceDashboardPeriod,
  createRangePeriod
} from 'utils/attendanceDashboardPeriod';
import './index.scss';

export type CalendarVariant = 'admin' | 'employee';

export interface AttendanceCalendarCoreProps {
  variant: CalendarVariant;
  epfNumber: string;
  employeeName: string;
  recordsByDate: Map<string, DayRecord>;
  legendItems: { className: string; label: string }[];
  statusLabels?: Record<DayStatus, string>;
  loading?: boolean;
  error?: string | null;
  viewMode: CalendarViewMode;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  rangeStart: Date;
  rangeEnd: Date;
  period?: AttendanceDashboardPeriod;
  onPeriodChange?: (period: AttendanceDashboardPeriod) => void;
  activeMonth: Date;
  onActiveMonthChange: (month: Date) => void;
  minimumOfficeDays?: number;
  onBack?: () => void;
  backLabel?: string;
  onExport?: () => void;
  headerTitle?: string;
  crumbs?: AttendanceTrackingCrumb[];
  lastUpdatedLabel?: string | null;
}

export const AttendanceCalendarCore = ({
  variant,
  epfNumber,
  employeeName,
  recordsByDate,
  legendItems,
  statusLabels = STATUS_LABELS,
  loading = false,
  error = null,
  viewMode,
  onViewModeChange,
  rangeStart,
  rangeEnd,
  period,
  onPeriodChange,
  activeMonth,
  onActiveMonthChange,
  minimumOfficeDays = 8,
  onBack,
  backLabel = 'Back',
  onExport,
  headerTitle,
  crumbs,
  lastUpdatedLabel,
  showViewModeToggle = variant === 'employee'
}: AttendanceCalendarCoreProps & { showViewModeToggle?: boolean }) => {
  const calendarFrameRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  const activeMonthKeyRef = useRef(format(activeMonth, 'yyyy-MM'));
  const monthBlockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const monthSectionsRef = useRef<MonthSection[]>([]);
  const syncFrameRef = useRef(0);

  const [hoveredCellKey, setHoveredCellKey] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const [viewportTick, setViewportTick] = useState(0);

  const isMonthlyView = viewMode === 'monthly';
  const isRangeView = viewMode === 'range';

  activeMonthKeyRef.current = format(activeMonth, 'yyyy-MM');

  const setCalendarScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollParent(el);
  }, []);

  // Probe line at ~40% of the scroll viewport height (scales with screen size).
  // Active month = last month whose block top has crossed this line.
  const MONTH_PROBE_RATIO = 0.4;

  const monthSections: MonthSection[] = useMemo(
    () =>
      getMonthsInView(rangeStart, rangeEnd).map((month) => ({
        month,
        monthKey: format(month, 'yyyy-MM'),
        cells: buildMonthCells(month, recordsByDate, rangeStart, rangeEnd)
      })),
    [recordsByDate, rangeStart, rangeEnd]
  );

  monthSectionsRef.current = monthSections;

  const syncActiveMonthFromProbe = useCallback(() => {
    const scrollEl = scrollRef.current;
    const sections = monthSectionsRef.current;
    if (!scrollEl || sections.length === 0) return;

    const rootRect = scrollEl.getBoundingClientRect();
    if (rootRect.height <= 0) return;

    // Viewport-relative probe: works the same on short and tall scroll areas.
    const probeY = rootRect.top + rootRect.height * MONTH_PROBE_RATIO;

    let nextMonth = sections[0].month;
    let nextKey = sections[0].monthKey;

    for (const section of sections) {
      const block = monthBlockRefs.current[section.monthKey];
      if (!block) continue;
      // Once a month's top has reached/passed the probe, it is the candidate.
      if (block.getBoundingClientRect().top <= probeY) {
        nextMonth = section.month;
        nextKey = section.monthKey;
      }
    }

    if (activeMonthKeyRef.current === nextKey) return;
    onActiveMonthChange(nextMonth);
  }, [onActiveMonthChange]);

  const scheduleActiveMonthSync = useCallback(() => {
    cancelAnimationFrame(syncFrameRef.current);
    syncFrameRef.current = requestAnimationFrame(syncActiveMonthFromProbe);
  }, [syncActiveMonthFromProbe]);

  const displayedSections = useMemo(() => {
    if (!isMonthlyView) return monthSections;
    const match =
      monthSections.find((section) =>
        isSameMonth(section.month, activeMonth)
      ) ?? monthSections[0];
    return match ? [match] : [];
  }, [activeMonth, isMonthlyView, monthSections]);

  const summaryRecords = useMemo(() => {
    if (isMonthlyView) {
      return getRecordsForMonth(
        recordsByDate,
        activeMonth,
        rangeStart,
        rangeEnd
      );
    }
    return getRecordsInRange(recordsByDate, rangeStart, rangeEnd);
  }, [activeMonth, isMonthlyView, recordsByDate, rangeStart, rangeEnd]);

  const summary = useMemo(
    () => computeSummary(summaryRecords),
    [summaryRecords]
  );

  const monthIndex = monthSections.findIndex((section) =>
    isSameMonth(section.month, activeMonth)
  );

  const canGoPreviousMonth = isMonthlyView || monthIndex > 0;
  const canGoNextMonth =
    isMonthlyView || (monthIndex >= 0 && monthIndex < monthSections.length - 1);

  const handlePreviousMonth = () => {
    if (isMonthlyView) {
      onActiveMonthChange(startOfMonth(subMonths(activeMonth, 1)));
      return;
    }

    if (!canGoPreviousMonth) return;
    onActiveMonthChange(monthSections[monthIndex - 1].month);
  };

  const handleNextMonth = () => {
    if (isMonthlyView) {
      onActiveMonthChange(startOfMonth(addMonths(activeMonth, 1)));
      return;
    }

    if (!canGoNextMonth) return;
    onActiveMonthChange(monthSections[monthIndex + 1].month);
  };

  useEffect(() => {
    if (!isRangeView) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const resetScrollToTop = () => {
      scrollEl.scrollTop = 0;
    };

    resetScrollToTop();
    requestAnimationFrame(() => {
      resetScrollToTop();
      scheduleActiveMonthSync();
    });
  }, [epfNumber, monthSections, isRangeView, rangeStart, rangeEnd, scheduleActiveMonthSync]);

  // Responsive layout sync for range view:
  // 1) Never shorter than one full month (no cut-off tiles)
  // 2) Prefer matching Status Legend card height when it is taller
  // 3) Cap by viewport so short screens still scroll cleanly
  // 4) Publish --month-snap-height for mandatory month snap
  useEffect(() => {
    if (!isRangeView) {
      const frame = calendarFrameRef.current;
      if (frame) frame.style.removeProperty('--calendar-frame-height');
      return;
    }

    const scrollEl = scrollRef.current;
    const frame = calendarFrameRef.current;
    const panel = panelRef.current;
    const weekdaysEl = frame?.querySelector(
      '.employee-attendance-calendar-page__weekdays'
    ) as HTMLElement | null;

    const measureMinFrameForFullMonth = () => {
      if (!frame) return 0;

      const weekdaysHeight = weekdaysEl?.getBoundingClientRect().height ?? 0;
      let tallestMonthContent = 0;

      for (const section of monthSectionsRef.current) {
        const block = monthBlockRefs.current[section.monthKey];
        const grid = block?.querySelector(
          '.employee-attendance-calendar-page__grid'
        ) as HTMLElement | null;
        if (!grid) continue;
        tallestMonthContent = Math.max(
          tallestMonthContent,
          grid.getBoundingClientRect().height
        );
      }

      // Keep the 16px month gaps inside the snapped month height.
      const monthChrome = 16 + 16;
      return Math.ceil(weekdaysHeight + tallestMonthContent + monthChrome);
    };

    const applySnapHeight = () => {
      if (scrollEl) {
        scrollEl.style.setProperty(
          '--month-snap-height',
          `${scrollEl.clientHeight}px`
        );
      }
    };

    const syncLayoutHeights = () => {
      if (!frame) return;

      const isStacked = window.matchMedia('(max-width: 1024px)').matches;
      const minFullMonthHeight = measureMinFrameForFullMonth();
      const legendHeight = panel
        ? Math.round(panel.getBoundingClientRect().height)
        : 0;
      const viewportCap = Math.round(
        Math.min(window.innerHeight * 0.7, 640)
      );

      // Floor: always fit one full month (never crop mid-tiles).
      const floor =
        minFullMonthHeight > 0
          ? minFullMonthHeight
          : Math.min(280, viewportCap);
      // Ceiling: prefer viewport cap, but never force below a full month.
      const ceiling = Math.max(floor, viewportCap);
      // Desktop: grow toward legend when it is taller; stacked: month only.
      const target = isStacked
        ? floor
        : Math.max(floor, legendHeight);
      const frameHeight = Math.min(target, ceiling);

      if (frameHeight > 0) {
        frame.style.setProperty('--calendar-frame-height', `${frameHeight}px`);
      }

      applySnapHeight();
    };

    const remountWaypoints = () => {
      setHoveredCellKey(null);
      setViewportTick((tick) => tick + 1);
    };

    const finalizeLayout = () => {
      syncLayoutHeights();
      // Second pass after height CSS applies, so snap height is exact.
      requestAnimationFrame(() => {
        applySnapHeight();
        scheduleActiveMonthSync();
      });
    };

    const handleWindowResize = () => {
      remountWaypoints();
      finalizeLayout();
    };

    const rafId = requestAnimationFrame(finalizeLayout);
    window.addEventListener('resize', handleWindowResize);
    window.visualViewport?.addEventListener('resize', handleWindowResize);

    // Observe content/legend size — not the frame itself (avoids RO feedback loops).
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            finalizeLayout();
          })
        : null;
    if (resizeObserver) {
      if (panel) resizeObserver.observe(panel);
      if (weekdaysEl) resizeObserver.observe(weekdaysEl);
      monthSectionsRef.current.forEach((section) => {
        const block = monthBlockRefs.current[section.monthKey];
        const grid = block?.querySelector(
          '.employee-attendance-calendar-page__grid'
        );
        if (grid) resizeObserver.observe(grid);
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleWindowResize);
      window.visualViewport?.removeEventListener('resize', handleWindowResize);
      resizeObserver?.disconnect();
      cancelAnimationFrame(syncFrameRef.current);
      frame?.style.removeProperty('--calendar-frame-height');
    };
  }, [
    isRangeView,
    scrollParent,
    scheduleActiveMonthSync,
    legendItems.length,
    loading,
    monthSections
  ]);

  const hoveredCell = useMemo(() => {
    for (const section of displayedSections) {
      const match = section.cells.find(
        (cell) =>
          cell.record && format(cell.date, 'yyyy-MM-dd') === hoveredCellKey
      );
      if (match) return match;
    }
    return null;
  }, [displayedSections, hoveredCellKey]);

  const viewRangeLabel = formatViewRangeLabel(rangeStart, rangeEnd);
  const pageClassName = `employee-attendance-calendar-page${
    variant === 'employee' ? ' employee-attendance-calendar-page--employee' : ''
  }${isMonthlyView ? ' employee-attendance-calendar-page--monthly' : ''}`;

  const showTooltipForCell = (cell: CalendarCell, element: HTMLElement) => {
    if (
      cell.isPadding ||
      cell.kind === 'Weekend' ||
      cell.kind === 'OutOfRange'
    ) {
      setHoveredCellKey(null);
      return;
    }

    setHoveredCellKey(format(cell.date, 'yyyy-MM-dd'));
    setTooltipPosition(positionTooltipNearCell(element));
  };

  const renderCell = (cell: CalendarCell, sectionKey: string) => {
    const cellKey = `${sectionKey}-${format(cell.date, 'yyyy-MM-dd')}`;
    const modifier = getCellModifier(cell.kind);
    const isInteractive =
      !cell.isPadding && cell.kind !== 'Weekend' && cell.kind !== 'OutOfRange';
    const showContent = !cell.isPadding && cell.kind !== 'OutOfRange';

    return (
      <button
        key={cellKey}
        type="button"
        className={`employee-attendance-calendar-page__cell employee-attendance-calendar-page__cell--${modifier}${
          isInteractive
            ? ' employee-attendance-calendar-page__cell--interactive'
            : ''
        }`}
        onMouseEnter={(event) => showTooltipForCell(cell, event.currentTarget)}
        onMouseLeave={() => setHoveredCellKey(null)}
        onFocus={(event) => showTooltipForCell(cell, event.currentTarget)}
        onBlur={() => setHoveredCellKey(null)}
        disabled={!isInteractive}
        tabIndex={isInteractive ? 0 : -1}
        aria-label={
          isInteractive
            ? `${format(cell.date, 'EEEE d MMMM')}, ${cell.label}`
            : undefined
        }
      >
        {showContent && (
          <span className="employee-attendance-calendar-page__cell-day">
            {format(cell.date, 'd')}
          </span>
        )}
        {showContent && cell.label && (
          <span className="employee-attendance-calendar-page__cell-body">
            <span className="employee-attendance-calendar-page__cell-status">
              {cell.label}
            </span>
            {cell.record &&
              cell.record.hours !== '—' &&
              cell.record.hours.length > 0 && (
                <span className="employee-attendance-calendar-page__cell-hours">
                  {cell.record.hours}
                </span>
              )}
          </span>
        )}
      </button>
    );
  };

  const headerAction = (
    <div className="employee-attendance-calendar-page__header-actions">
      {showViewModeToggle && onViewModeChange && (
        <CalendarViewModeToggle value={viewMode} onChange={onViewModeChange} />
      )}
      {isRangeView && period && onPeriodChange && (
        <AttendanceDashboardPeriodFilter
          period={period}
          onChange={onPeriodChange}
        />
      )}
      {variant === 'admin' && isRangeView && !onPeriodChange && (
        <div className="employee-attendance-calendar-page__date-range">
          <CalendarMonthOutlinedIcon fontSize="small" aria-hidden />
          <span>{viewRangeLabel}</span>
        </div>
      )}
    </div>
  );

  const title =
    headerTitle ??
    (variant === 'employee' ? 'My Attendance' : employeeName);

  return (
    <div className={pageClassName}>
      {variant === 'admin' ? (
        <AttendanceTrackingPageHeader
          title={title}
          crumbs={crumbs}
          action={headerAction}
        />
      ) : (
        <div className="employee-attendance-calendar-page__employee-header">
          <h1 className="employee-attendance-calendar-page__employee-title">
            {title}
          </h1>
          <div className="employee-attendance-calendar-page__header-actions-col">
            {headerAction}
            {lastUpdatedLabel && (
              <span className="employee-attendance-calendar-page__last-updated">
                Last updated: {lastUpdatedLabel}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="employee-attendance-calendar-page__body">
        {loading ? (
          <div className="employee-attendance-calendar-page__state">
            Loading attendance calendar...
          </div>
        ) : error ? (
          <div className="employee-attendance-calendar-page__state employee-attendance-calendar-page__state--error">
            {error}
          </div>
        ) : (
          <>
            {isMonthlyView ? (
              <div className="employee-attendance-calendar-page__month-title-row">
                <CalendarMonthNavigator
                  month={activeMonth}
                  onPrevious={handlePreviousMonth}
                  onNext={handleNextMonth}
                  canGoPrevious={canGoPreviousMonth}
                  canGoNext={canGoNextMonth}
                />
              </div>
            ) : (
              <h2 className="employee-attendance-calendar-page__month-title">
                {format(activeMonth, 'MMMM yyyy')}
              </h2>
            )}

            <div
              ref={calendarFrameRef}
              className="employee-attendance-calendar-page__calendar-frame"
            >
              <div className="employee-attendance-calendar-page__weekdays">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="employee-attendance-calendar-page__weekday"
                  >
                    {day}
                  </div>
                ))}
              </div>

              <div
                ref={setCalendarScrollRef}
                className={`employee-attendance-calendar-page__calendar-scroll${
                  isMonthlyView
                    ? ' employee-attendance-calendar-page__calendar-scroll--monthly'
                    : ''
                }`}
                onScroll={() => {
                  setHoveredCellKey(null);
                  if (isRangeView) scheduleActiveMonthSync();
                }}
              >
                {displayedSections.map((section) => (
                  <div
                    key={section.monthKey}
                    ref={(el) => {
                      monthBlockRefs.current[section.monthKey] = el;
                    }}
                    className="employee-attendance-calendar-page__month-block"
                    data-month={section.monthKey}
                  >
                    {isRangeView && scrollParent && (
                      <Waypoint
                        key={`${section.monthKey}-${viewportTick}`}
                        scrollableAncestor={scrollParent}
                        // Wide band so scroll/resize continuously notifies us;
                        // the real month decision uses the viewport probe below.
                        topOffset="0%"
                        bottomOffset="0%"
                        fireOnRapidScroll
                        onPositionChange={scheduleActiveMonthSync}
                        onEnter={scheduleActiveMonthSync}
                        onLeave={scheduleActiveMonthSync}
                      />
                    )}
                    <div className="employee-attendance-calendar-page__grid">
                      {section.cells.map((cell) =>
                        renderCell(cell, section.monthKey)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="employee-attendance-calendar-page__rail">
              <section
                ref={panelRef}
                className="employee-attendance-calendar-page__panel"
              >
                {isMonthlyView && (
                  <>
                    <OfficeDaysProgressBar
                      officeDays={summary.officeDays}
                      threshold={minimumOfficeDays}
                    />
                    <div className="employee-attendance-calendar-page__panel-divider" />
                  </>
                )}

                <h3 className="employee-attendance-calendar-page__panel-title">
                  Status Legend
                </h3>
                <div className="employee-attendance-calendar-page__legend">
                  {legendItems.map((item) => (
                    <div
                      key={item.className}
                      className="employee-attendance-calendar-page__legend-item"
                    >
                      <span
                        className={`employee-attendance-calendar-page__legend-swatch employee-attendance-calendar-page__legend-swatch--${item.className}`}
                        aria-hidden
                      />
                      {item.label}
                    </div>
                  ))}
                </div>

                <div className="employee-attendance-calendar-page__panel-divider" />

                <h3 className="employee-attendance-calendar-page__panel-subtitle">
                  Summary
                  {isMonthlyView
                    ? ` — ${format(activeMonth, 'MMMM yyyy')}`
                    : ''}
                </h3>
                <ul className="employee-attendance-calendar-page__summary-list">
                  <li className="employee-attendance-calendar-page__summary-item">
                    <span>Work from office days</span>
                    <strong>{summary.officeDays}</strong>
                  </li>
                  <li className="employee-attendance-calendar-page__summary-item">
                    <span>Leave days</span>
                    <strong>{summary.leaves}</strong>
                  </li>
                  <li className="employee-attendance-calendar-page__summary-item">
                    <span>Missing days</span>
                    <strong>{summary.absent}</strong>
                  </li>
                  <li className="employee-attendance-calendar-page__summary-item">
                    <span>Total hours</span>
                    <strong>{summary.totalHours.toFixed(1)}</strong>
                  </li>
                </ul>
              </section>
            </aside>

            {(onBack || onExport) && (
              <footer className="employee-attendance-calendar-page__footer">
                {onBack && (
                  <button
                    type="button"
                    className="employee-attendance-calendar-page__footer-btn"
                    onClick={onBack}
                  >
                    <ChevronLeftIcon fontSize="small" aria-hidden />
                    {backLabel}
                  </button>
                )}
                {onExport && (
                  <button
                    type="button"
                    className="employee-attendance-calendar-page__footer-btn employee-attendance-calendar-page__footer-btn--primary"
                    onClick={onExport}
                  >
                    <FileDownloadOutlinedIcon fontSize="small" aria-hidden />
                    Export
                  </button>
                )}
              </footer>
            )}
          </>
        )}
      </div>

      {hoveredCell?.record &&
        createPortal(
          <div
            className="employee-attendance-calendar-page__tooltip"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left
            }}
            role="tooltip"
          >
            <p className="employee-attendance-calendar-page__tooltip-title">
              {format(hoveredCell.date, 'EEE d MMM')}
            </p>
            <dl className="employee-attendance-calendar-page__tooltip-body">
              {hasPunchDetails(hoveredCell.record) && (
                <>
                  <div className="employee-attendance-calendar-page__tooltip-row">
                    <dt>Arrival</dt>
                    <dd>{hoveredCell.record.arrival}</dd>
                  </div>
                  <div className="employee-attendance-calendar-page__tooltip-row">
                    <dt>Departure</dt>
                    <dd>{hoveredCell.record.departure}</dd>
                  </div>
                  <div className="employee-attendance-calendar-page__tooltip-row">
                    <dt>Hours</dt>
                    <dd>{formatTooltipHours(hoveredCell.record)}</dd>
                  </div>
                </>
              )}
              {hoveredCell.record.status === 'PublicHoliday' &&
              hoveredCell.record.publicHolidayName ? (
                <div className="employee-attendance-calendar-page__tooltip-row">
                  <dt>Holiday</dt>
                  <dd>{hoveredCell.record.publicHolidayName}</dd>
                </div>
              ) : null}
              <div className="employee-attendance-calendar-page__tooltip-row">
                <dt>Status</dt>
                <dd>{statusLabels[hoveredCell.record.status]}</dd>
              </div>
            </dl>
          </div>,
          document.body
        )}
    </div>
  );
};

export const getDefaultActiveMonth = (reference = new Date()) =>
  startOfMonth(reference);

export const shiftActiveMonthOnViewModeChange = (
  mode: CalendarViewMode,
  currentMonth: Date
) => {
  if (mode === 'monthly') {
    return startOfMonth(currentMonth);
  }
  return currentMonth;
};

export const getPeriodFromRange = (from: Date, to: Date) =>
  createRangePeriod(from, to);
